import { Popover } from "@radix-ui/react-popover";
import { format } from "date-fns";
import { CalendarIcon, Check, ChevronsUpDown } from "lucide-react";
import { InputHTMLAttributes, ReactNode, useEffect, useId, useRef, useState } from "react";

import { Calendar } from "@/components/ui/calendar";
import { cn, cx } from "@/utils/cx";

import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "./ui/command";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { PopoverContent, PopoverTrigger } from "./ui/popover";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

/**
 * @deprecated Use `Input` from `@/components/ui/input` instead.
 */
export const ControlledTextInput = ({
    value,
    onValue,
    id,
    label,
    placeholder,
    disabled = false,
    required = false,
    autoFocus,
    className,
    type = "text",
    validationError,
}: {
    value: string;
    onValue: (value: string) => void;
    id: string;
    label?: string;
    labelColor?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    autoFocus?: boolean;
    className?: string;
    type?: InputHTMLAttributes<HTMLInputElement>["type"];
    validationError?: string;
}) => (
    <div className={cx("mt-2 flex flex-col gap-2", className)}>
        {label && <Label htmlFor={id}>{label}</Label>}
        <Input
            id={id}
            type={type}
            placeholder={placeholder || label}
            value={value}
            onChange={e => onValue(e.target.value)}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            className="w-full max-w-full"
        />
        {validationError ? <div className="text-sm text-red-500">{validationError}</div> : null}
    </div>
);

/**
 * @deprecated Use `Input` from `@/components/ui/input` instead.
 */
export const ControlledTextArea = ({
    value,
    onValue,
    label,
    placeholder,
    id,
    disabled = false,
    required = false,
    autoFocus,
    className,
    validationError,
}: {
    value: string;
    onValue: (value: string) => void;
    id: string;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    autoFocus?: boolean;
    className?: string;
    type?: InputHTMLAttributes<HTMLInputElement>["type"];
    validationError?: string;
}) => (
    <div className={cx("flex flex-col space-y-2", className)}>
        {label && <Label htmlFor={id}>{label}</Label>}
        <Textarea
            placeholder={placeholder || label}
            value={value}
            onChange={e => onValue(e.target.value)}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            className={cx("resize-none", validationError && "border-red-500")}
            id={id}
        />
        {validationError ? <div className="text-sm text-red-500">{validationError}</div> : null}
    </div>
);

export type ControlledSelectInputOption = {
    value: string | null;
    label: ReactNode;
};

/**
 * @deprecated Use `Select` from `@/components/ui/select` instead.
 */
export const ControlledSelectInput = ({
    value,
    onValue,
    options,
    label,
    className,
    placeholder,
    validationError,
}: {
    value?: string | null;
    onValue: (value: string | null) => void;
    options: ControlledSelectInputOption[];
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    autoFocus?: boolean;
    className?: string;
    validationError?: string;
}) => {
    const val: ControlledSelectInputOption = {
        value: value || "",
        label: options.find(option => option.value === value)?.label || "",
    };

    return (
        <div className={cx("flex flex-col gap-2", className)}>
            <Label>{label}</Label>
            <Select
                onValueChange={val => onValue(val !== "empty" ? val : null)}
                defaultValue={val.value ?? ""}
            >
                <SelectTrigger>
                    <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                    <SelectGroup>
                        {options.map((opt, i) => (
                            <SelectItem key={i} value={opt.value ?? "empty"}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectGroup>
                </SelectContent>
            </Select>
            {validationError ? <div className="text-sm text-red-500">{validationError}</div> : null}
        </div>
    );
};

/**
 * @deprecated Use `Checkbox` from `@/components/ui/checkbox` instead.
 */
export const ControlledCheckboxInput = ({
    checked,
    onValue,
    label,
    disabled = false,
    className,
}: {
    checked: boolean | null;
    onValue: (checked: boolean) => void;
    label?: string;
    disabled?: boolean;
    className?: string;
}) => {
    const id = useId();

    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current && typeof checked !== "boolean") {
            ref.current.indeterminate = true;
        }
    }, [checked, ref]);

    return (
        <div className={cx("flex items-center", className)}>
            <Checkbox
                id={id}
                className={cx("mr-2 h-4 w-4", !disabled && "cursor-pointer")}
                checked={checked ?? false}
                onClick={() => onValue(!checked)}
                disabled={disabled}
            />
            {label && (
                <Label
                    htmlFor={id}
                    className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                    {label}
                </Label>
            )}
        </div>
    );
};

export const SearchAndSelectInput = ({
    value,
    onValueChange,
    label,
    suggestions,
    className,
}: {
    value: string | null;
    onValueChange: (value: string) => void;
    label?: string;
    suggestions: {
        label: string;
        value: string;
    }[];
    className?: string;
}) => {
    const [open, setOpen] = useState(false);

    return (
        <div className={cx("flex flex-col gap-2", className)}>
            {label && <Label>{label}</Label>}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="justify-between"
                    >
                        {value
                            ? suggestions.find(suggestion => suggestion.value === value)?.label
                            : "Select"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                    <Command>
                        <CommandInput placeholder="Search" />
                        <CommandList>
                            <CommandEmpty>Not found</CommandEmpty>
                            <CommandGroup>
                                {suggestions.map(suggestion => (
                                    <CommandItem
                                        key={suggestion.value}
                                        value={suggestion.value}
                                        onSelect={currentValue => {
                                            onValueChange(currentValue);
                                            setOpen(false);
                                        }}
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4",
                                                value === suggestion.value
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            )}
                                        />
                                        {suggestion.label}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export const DateInput = ({
    value,
    onValue,
    label,
    disabled = false,
    autoFocus,
    className,
    validationError,
}: {
    value: Date | null;
    onValue: (value?: Date) => void;
    label?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    className?: string;
    validationError?: string;
}) => {
    const id = useId();

    return (
        <div className={cx("flex flex-col gap-2", className)}>
            {label ? <Label htmlFor={id}>{label}</Label> : null}
            <Popover>
                <PopoverTrigger asChild disabled={disabled}>
                    <Button
                        variant={"outline"}
                        className={cn(
                            "w-[280px] justify-start text-left font-normal",
                            !value && "text-muted-foreground"
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {value ? format(value, "PPP") : <span>Pick a date</span>}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                    <Calendar
                        mode="single"
                        selected={value ?? undefined}
                        onSelect={onValue}
                        initialFocus={autoFocus}
                    />
                </PopoverContent>
            </Popover>
            {validationError ? <div className="text-sm text-red-500">{validationError}</div> : null}
        </div>
    );
};
